import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { History, Loader2 } from 'lucide-react';
import Modal from './Modal';
import Alert from './Alert';
import {
    documentOperationScopeKey,
    listDocumentOperations,
    resumeDocumentOperation,
    undoDocumentOperation,
} from '../services/documentOperationsApi';
import { DOCUMENT_OPERATION_UNDO_RECEIPT_EVENT } from '../utils/documentOperationUndo';

const OPERATION_LABELS = Object.freeze({
    upload: 'Upload',
    copy: 'Copy',
    move: 'Move',
    delete: 'Delete',
});
const STATUS_LABELS = Object.freeze({
    available: 'Completed',
    complete: 'Completed',
    completed: 'Completed',
    pending: 'In progress',
    running: 'In progress',
    needs_review: 'Needs review',
    undoing: 'Undo in progress',
    undone: 'Undone',
    failed: 'Failed',
    expired: 'Expired',
});

const operationTime = (value) => {
    if (!value) return 'Time unavailable';
    const parsed = new Date(String(value).replace(' ', 'T'));
    if (Number.isNaN(parsed.getTime())) return 'Time unavailable';
    return parsed.toLocaleString([], {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit',
    });
};

export const DocumentRecentActions = ({ caseId, userId, onChanged }) => {
    const scopeKey = documentOperationScopeKey({ caseId, userId });
    return (
        <ScopedDocumentRecentActions
            key={scopeKey}
            caseId={caseId}
            userId={userId}
            onChanged={onChanged}
        />
    );
};

const ScopedDocumentRecentActions = ({ caseId, userId, onChanged }) => {
    const scope = useMemo(() => ({ caseId: String(caseId ?? ''), userId: String(userId ?? '') }), [caseId, userId]);
    const scopeKey = documentOperationScopeKey(scope);
    const scopeLifetime = useMemo(() => ({}), [scopeKey]);
    const currentLifetimeRef = useRef(scopeLifetime);
    currentLifetimeRef.current = scopeLifetime;
    const mountedRef = useRef(false);
    const historyControllerRef = useRef(null);
    const undoControllerRef = useRef(null);
    const historyGenerationRef = useRef(0);
    const undoInFlightRef = useRef(null);
    const triggerRef = useRef(null);
    const dialogRef = useRef(null);
    const [operations, setOperations] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [busyReceiptId, setBusyReceiptId] = useState(null);
    const validScope = /^\d+$/.test(scope.caseId) && /^\d+$/.test(scope.userId);

    const isCurrent = useCallback((capturedLifetime) => (
        mountedRef.current && currentLifetimeRef.current === capturedLifetime
    ), []);

    const load = useCallback(async () => {
        if (!validScope || !isCurrent(scopeLifetime)) return;
        historyControllerRef.current?.abort();
        const controller = new AbortController();
        historyControllerRef.current = controller;
        const generation = ++historyGenerationRef.current;
        const current = () => !controller.signal.aborted
            && isCurrent(scopeLifetime)
            && generation === historyGenerationRef.current;

        setLoading(true);
        setError('');
        try {
            const rows = await listDocumentOperations(scope, { signal: controller.signal });
            if (!current()) return;
            setOperations(rows);
        } catch (requestError) {
            if (!current() || requestError?.name === 'AbortError') return;
            // A denial or uncertain response must not leave previously visible
            // rows actionable under the current identity.
            setOperations([]);
            setError(requestError?.message || 'Recent document actions are unavailable.');
        } finally {
            if (current()) setLoading(false);
        }
    }, [isCurrent, scope, scopeLifetime, validScope]);

    useEffect(() => {
        mountedRef.current = true;
        const handleReceipt = (event) => {
            const receipt = event?.detail;
            if (!receipt?.receiptId
                || String(receipt.caseId ?? '') !== scope.caseId
                || (receipt.userId != null && String(receipt.userId) !== scope.userId)
                || !isCurrent(scopeLifetime)) return;
            // Never trust the event as history. It is only a prompt to fetch the
            // authenticated current-user list from the server.
            load();
        };
        window.addEventListener(DOCUMENT_OPERATION_UNDO_RECEIPT_EVENT, handleReceipt);
        // Load on entry so interrupted work is visible from the toolbar badge
        // without requiring the user to discover and open the dialog first.
        load();
        return () => {
            mountedRef.current = false;
            historyGenerationRef.current += 1;
            historyControllerRef.current?.abort();
            undoControllerRef.current?.abort();
            undoInFlightRef.current = null;
            window.removeEventListener(DOCUMENT_OPERATION_UNDO_RECEIPT_EVENT, handleReceipt);
        };
    }, [isCurrent, load, scope.caseId, scope.userId, scopeLifetime]);

    const close = () => {
        setOpen(false);
        window.setTimeout(() => triggerRef.current?.focus(), 0);
    };

    useEffect(() => {
        if (!open) return undefined;
        const focusable = dialogRef.current?.querySelector('button:not(:disabled)');
        focusable?.focus();
        return undefined;
    }, [open, loading, busyReceiptId]);

    const handleDialogKeyDown = (event) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            close();
            return;
        }
        if (event.key !== 'Tab') return;
        const buttons = Array.from(
            dialogRef.current?.querySelectorAll('button:not(:disabled)') || []
        );
        const first = buttons[0];
        const last = buttons[buttons.length - 1];
        if (!first) {
            event.preventDefault();
            return;
        }
        if (event.shiftKey
            && (document.activeElement === first || !dialogRef.current?.contains(document.activeElement))) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    };

    const undo = async (operation) => {
        if (!operation?.canUndo || undoInFlightRef.current || loading
            || !operations.some(item => item.receiptId === operation.receiptId)) return;
        const controller = new AbortController();
        undoControllerRef.current = controller;
        undoInFlightRef.current = operation.receiptId;
        setBusyReceiptId(operation.receiptId);
        setError('');
        const capturedLifetime = scopeLifetime;
        try {
            const result = await undoDocumentOperation(scope, operation.receiptId, { signal: controller.signal });
            if (!isCurrent(capturedLifetime) || controller.signal.aborted) return;
            // Disable the committed inverse immediately so a slow listing
            // refresh cannot allow a second click.
            setOperations(current => current.map(item => (
                item.receiptId === operation.receiptId
                    ? { ...item, canUndo: false, status: 'undone' }
                    : item
            )));
            if (isCurrent(capturedLifetime) && !controller.signal.aborted) await load();
            if (isCurrent(capturedLifetime) && !controller.signal.aborted) {
                // Receipt state is authoritative and refreshes first. Folder
                // and tree reconciliation is best effort and must not keep the
                // operation controls globally busy if a view request stalls.
                Promise.resolve()
                    .then(() => onChanged?.(result))
                    .catch(() => {});
            }
        } catch (requestError) {
            if (isCurrent(capturedLifetime) && requestError?.name !== 'AbortError') {
                const message = requestError?.message || 'CaseDB could not safely undo that action.';
                // Revalidate after an uncertain inverse instead of leaving a
                // possibly stale Undo control active.
                await load();
                if (isCurrent(capturedLifetime)) setError(message);
            }
        } finally {
            if (isCurrent(capturedLifetime) && undoInFlightRef.current === operation.receiptId) {
                undoInFlightRef.current = null;
                setBusyReceiptId(null);
            }
        }
    };

    const resume = async (operation) => {
        if (!operation?.canResume || undoInFlightRef.current || loading
            || !operations.some(item => item.receiptId === operation.receiptId)) return;
        const controller = new AbortController();
        undoControllerRef.current = controller;
        undoInFlightRef.current = operation.receiptId;
        setBusyReceiptId(operation.receiptId);
        setError('');
        const capturedLifetime = scopeLifetime;
        try {
            // The server owns the recovery direction. A needs-review receipt
            // can represent either an interrupted delete or an interrupted
            // undo, so the browser must never guess from its public status.
            const result = await resumeDocumentOperation(
                scope,
                operation.receiptId,
                { signal: controller.signal }
            );
            if (!isCurrent(capturedLifetime) || controller.signal.aborted) return;
            // Remove the action immediately. The server refresh below decides
            // whether it is now available, complete, or still needs review.
            setOperations(current => current.map(item => (
                item.receiptId === operation.receiptId
                    ? { ...item, canResume: false, status: 'running' }
                    : item
            )));
            if (isCurrent(capturedLifetime) && !controller.signal.aborted) await load();
            if (isCurrent(capturedLifetime) && !controller.signal.aborted) {
                Promise.resolve()
                    .then(() => onChanged?.(result))
                    .catch(() => {});
            }
        } catch (requestError) {
            if (isCurrent(capturedLifetime) && requestError?.name !== 'AbortError') {
                const message = requestError?.message || 'CaseDB could not safely resume that action.';
                await load();
                if (isCurrent(capturedLifetime)) setError(message);
            }
        } finally {
            if (isCurrent(capturedLifetime) && undoInFlightRef.current === operation.receiptId) {
                undoInFlightRef.current = null;
                setBusyReceiptId(null);
            }
        }
    };

    return (
        <>
            <button
                ref={triggerRef}
                type="button"
                className="action quaternary small-text document-toolbar-icon-button document-recent-actions-trigger"
                aria-label={`Recent document actions${operations.length > 0
                    ? `, ${operations.length} recorded`
                    : ''}`}
                aria-haspopup="dialog"
                aria-expanded={open}
                title="Recent document actions"
                disabled={!validScope}
                onClick={() => {
                    setOpen(true);
                    // Opening always revalidates current server state. The
                    // mount-time load is only a badge hint and may be stale or
                    // may have been interrupted in another tab.
                    load();
                }}
            >
                <History size={18} aria-hidden="true" />
                {operations.length > 0 && (
                    <span className="document-recent-actions-count" aria-hidden="true">
                        {Math.min(operations.length, 99)}
                    </span>
                )}
            </button>
            {open && createPortal(
                <div
                    ref={dialogRef}
                    role="dialog"
                    aria-modal="true"
                    aria-label="Recent document actions"
                    onKeyDown={handleDialogKeyDown}
                >
                    <Modal
                        title="Recent document actions"
                        instructions="Your recent changes in this case."
                        onClose={close}
                        footer={(
                            <div className="modal-footer-actions">
                                <button type="button" className="action alt" onClick={load} disabled={loading || Boolean(busyReceiptId)}>
                                    Refresh
                                </button>
                                <button type="button" className="action" onClick={close}>Close</button>
                            </div>
                        )}
                    >
                        <div className="modal-content-wrapper document-recent-actions-modal">
                            {error && <Alert message={error} type="warning" onClose={() => setError('')} />}
                            {loading && (
                                <div className="document-recent-actions-state small-text" role="status">
                                    <Loader2 size={18} aria-hidden="true" />
                                    Loading recent actions…
                                </div>
                            )}
                            {!loading && !error && operations.length === 0 && (
                                <div className="document-recent-actions-state small-text">
                                    <History size={22} aria-hidden="true" />
                                    No recent document actions for this case.
                                </div>
                            )}
                            {!loading && operations.length > 0 && (
                                <div className="document-recent-actions-list">
                                    {operations.map(operation => (
                                        <div className="document-recent-actions-row" key={operation.receiptId}>
                                            <div className="document-recent-actions-details">
                                                <div className="document-recent-actions-heading">
                                                    <strong>{OPERATION_LABELS[operation.operation]}</strong>
                                                    <span className={`document-recent-actions-status is-${operation.status}`}>
                                                        {operation.cleanupPending
                                                            ? 'Finishing cleanup'
                                                            : (STATUS_LABELS[operation.status] || 'Recorded')}
                                                    </span>
                                                </div>
                                                <span className="small-text">
                                                    {operation.objectCount.toLocaleString()} {operation.objectCount === 1 ? 'item' : 'items'} · {operationTime(operation.createdAt)}
                                                </span>
                                            </div>
                                            {operation.canUndo && (
                                                <button
                                                    type="button"
                                                    className="action small-text"
                                                    disabled={Boolean(busyReceiptId) || loading}
                                                    onClick={() => undo(operation)}
                                                >
                                                    {busyReceiptId === operation.receiptId ? 'Undoing…' : 'Undo'}
                                                </button>
                                            )}
                                            {!operation.canUndo && operation.canResume && (
                                                <button
                                                    type="button"
                                                    className="action small-text"
                                                    disabled={Boolean(busyReceiptId) || loading}
                                                    onClick={() => resume(operation)}
                                                >
                                                    {busyReceiptId === operation.receiptId
                                                        ? 'Resuming…'
                                                        : operation.resumeLabel}
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </Modal>
                </div>,
                document.body
            )}
        </>
    );
};

export default DocumentRecentActions;
